import React, { useState, useEffect } from 'react'
import { PageHeader, Button, Table, Skeleton, Popconfirm, Modal } from 'antd'
import { useQuery } from '@apollo/react-hooks'
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons'

import ResultError from '@packages/ResultError'
import { IRRIGATION_TYPES_ICON } from '@constants/IconsConstants'
import { useColumnFilterProps } from '@utils/columnFilter'
import { getIrrigationTypesQuery } from '@graphql/queries'
import { useUpdateIrrigationType, useDeleteIrrigationType } from '@graphql/mutations'

import EditableCell from '../../EditableCell'
import ManageIrrigationTypeForm from '../ManageIrrigationTypeForm'

const tableProps = {
  size: 'middle',
  pagination: false,
}
const newButtonProps = {
  type: 'primary',
  icon: <PlusOutlined />,
  key: 'newIrrigationType',
}

function ManageIrrigationTypeTable() {
  const [irrigationTypesData, setIrrigationTypesData] = useState(false)
  const [formModalVisible, setFormModalVisible] = useState(false)
  const { error, data } = useQuery(getIrrigationTypesQuery)
  const [updateIrrigationType] = useUpdateIrrigationType()
  const [deleteIrrigationType] = useDeleteIrrigationType()
  const useFilterField = field => useColumnFilterProps(field)
  const toggleFormModal = () => setFormModalVisible(!formModalVisible)

  useEffect(() => {
    if (data) setIrrigationTypesData(data.irrigationTypes)
  }, [data])

  const handleSave = data => {
    const { id, name, efficiency } = data
    const newData = [...irrigationTypesData]
    const index = newData.findIndex(item => item.id === data.id)
    const item = newData[index]

    newData.splice(index, 1, { ...item, ...data })

    if (JSON.stringify(newData) !== JSON.stringify(irrigationTypesData)) {
      updateIrrigationType({
        variables: {
          id,
          data: {
            name: { set: name },
            efficiency: { set: Number(efficiency) },
          },
        },
      })

      setIrrigationTypesData(newData)
    }
  }

  const handleDelete = id => {
    deleteIrrigationType({
      variables: { id },
      refetchQueries: ['GetIrrigationTypesQuery'],
    })
  }
  const columns = [
    {
      dataIndex: 'name',
      title: 'Nome',
      editable: true,
      ...useFilterField('name'),
    },
    {
      dataIndex: 'efficiency',
      title: 'Eficiência',
      editable: true,
      ...useFilterField('efficiency'),
    },
    {
      align: 'center',
      render: (value, { id, name }) => (
        <Popconfirm
          title={`Tem a certeza que quer eliminar ${name} ?`}
          onConfirm={() => handleDelete(id)}
          okText="Sim"
          cancelText="Não"
        >
          <DeleteOutlined />
        </Popconfirm>
      ),
    },
  ]
  const mappedColumns = columns.map(col => {
    if (!col.editable) return col

    return {
      ...col,
      onCell: record => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    }
  })

  return (
    <>
      <Modal
        title="Novo tipo de rega"
        visible={formModalVisible}
        onCancel={() => toggleFormModal()}
        footer={null}
      >
        <ManageIrrigationTypeForm afterSubmit={() => toggleFormModal()} />
      </Modal>

      <PageHeader
        title="Tipos de Rega"
        avatar={{ src: IRRIGATION_TYPES_ICON }}
        extra={[
          <Button {...newButtonProps} onClick={() => toggleFormModal()}>
            Novo tipo de rega
          </Button>,
        ]}
      />

      {error && <ResultError />}

      <Skeleton loading={!error && !irrigationTypesData} active>
        {irrigationTypesData && (
          <Table
            components={EditableCell.Components}
            columns={mappedColumns}
            dataSource={irrigationTypesData}
            rowKey={({ id }) => id}
            rowClassName={() => 'editable-row'}
            {...tableProps}
          />
        )}
      </Skeleton>
    </>
  )
}

export default ManageIrrigationTypeTable
